/**
 * **D61's 24-hour window — subsystem 3, and the sentence it must say.**
 *
 * > A cached BLE credential is honoured for at most 24 hours since the last successful server
 * > sync. Past that, the local open is refused.
 *
 * Four rules, each of which is a decision rather than a tuning knob:
 *
 *  - **The clock starts at the last successful *server* sync**, not at the credential fetch and
 *    not at the last BLE open. A credential obtained an hour ago on a device that last reached
 *    the server two days ago is **stale**, because what the window bounds is how long a *revoked*
 *    grant can keep working — and only a server round trip can tell us a grant still exists.
 *  - **The window bounds revocation latency, and D61 names the trade honestly:** *"a removed
 *    resident retains BLE for at most a day, which is the accepted cost of BLE working during a
 *    network outage at all."* **Do not shorten it to feel safer** — the number is the decision,
 *    and shortening it silently converts a specified capability into an unreliable one.
 *  - 🔸 **Stage 2 native may shorten it** if it can bind a session proof (Q12). **Never lengthen
 *    it**, and `assertWindowNotLengthened()` below is that rule as code.
 *  - **Cached Home UI is unaffected** (PEC-14). It already grants no authority; D61 governs the
 *    offline BLE path alone, and nothing here is consulted by anything that renders a gate list.
 *
 * **Where the clock is read from, and why that is conservative.** The parent PRD says *"P2's
 * session refresh and P3/P4's gate reads are what advance it."* This module reads the device
 * cache's `fetchedAt` — the instant this launch's `GET /gates` last produced a snapshot, which is
 * precisely a P3 gate read completed under a live session. It advances **less often** than the
 * full set the PRD names, which is the safe direction and the only one worth being wrong in: a
 * conservative clock can only make the window *end sooner*, never later. Logged as an Assumption
 * (`implementation-notes.md` → P16) rather than treated as an equivalence.
 *
 * **This module holds no credential and touches no radio.** It answers one question, and it
 * answers it the same way whether or not a credential happens to be in memory — which is what
 * makes the refusal below reachable on a device that never fetched one.
 */

/**
 * **24 hours. D61's number, and the only place it appears.**
 *
 * `device-cache.md` states it twice (the native-credential row and startup step 9),
 * `edge-cases.md` states it as PEC-26, and §13 tests it. All four describe this constant.
 */
export const FRESH_GRANT_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * Why a local open may not proceed — and **each value is a different sentence to the resident**,
 * which is the whole reason this is a union rather than a boolean.
 *
 * PEC-25/D84 fix the wording for `stale`: *local access needs reconnection or service*. Never
 * "gate not found", and never a silent failure — *"a refusal that misnames the cause sends a
 * resident to support with the wrong problem."*
 */
export type FreshnessVerdict =
  /** Inside the window. A local open may proceed if everything else permits it. */
  | { readonly ok: true; readonly expiresAt: number }
  /**
   * **Past D61's window.** The device has not reached the server in 24 hours, so nothing here can
   * still claim the grant exists.
   */
  | { readonly ok: false; readonly reason: 'stale'; readonly since: number }
  /**
   * **The server has never been reached on this device for this account.** Distinct from `stale`
   * on purpose: there is no window to be past, and a first-launch-offline resident is not someone
   * whose access "expired". The copy differs, and conflating the two would tell a brand-new user
   * that something has lapsed.
   */
  | { readonly ok: false; readonly reason: 'never-synced' }

/**
 * Evaluate D61 for one device.
 *
 * @param lastSyncAt the ISO instant of the last successful server sync, or `undefined` when this
 *   device has never completed one for this account.
 */
export function evaluateFreshness(
  lastSyncAt: string | undefined,
  now: number = Date.now(),
  windowMs: number = FRESH_GRANT_WINDOW_MS,
): FreshnessVerdict {
  assertWindowNotLengthened(windowMs)
  if (lastSyncAt === undefined) return { ok: false, reason: 'never-synced' }

  const since = Date.parse(lastSyncAt)
  /*
   * An unparseable stamp is treated as never having synced rather than as a fresh one. **Failing
   * closed is the only defensible direction here**: the alternative reading of a corrupt value is
   * "this device may open gates locally", and a corrupt snapshot is exactly the state in which
   * that claim is least supportable.
   */
  if (!Number.isFinite(since)) return { ok: false, reason: 'never-synced' }

  /*
   * A stamp in the future means the device's clock moved backwards since the snapshot was
   * written — a real occurrence on a phone that syncs time after a long offline stretch. It is
   * treated as fresh, because the value came from *our own* successful server round trip and the
   * only alternative is to refuse local access for a clock correction the resident cannot see.
   * The exposure is bounded by the same 24 hours once the clock settles.
   */
  const elapsed = now - since
  if (elapsed >= windowMs) return { ok: false, reason: 'stale', since }
  return { ok: true, expiresAt: since + windowMs }
}

/**
 * **Q12, as code.** Stage 2 native may shorten the window if it can bind a session proof; nothing
 * may lengthen it.
 *
 * A parameter exists at all so a test can exercise the boundary without waiting a day, and so the
 * Stage-2 shortening is a call-site change rather than an edit to the constant. The guard is what
 * stops that same parameter from becoming the way the decision is quietly reversed — a caller
 * passing `48 * 60 * 60 * 1000` would look like configuration and would be D61 repealed.
 */
export function assertWindowNotLengthened(windowMs: number): void {
  if (windowMs > FRESH_GRANT_WINDOW_MS) {
    throw new RangeError(
      'D61: the offline BLE window may be shortened (Q12) and never lengthened — ' +
        `${windowMs}ms exceeds the specified ${FRESH_GRANT_WINDOW_MS}ms`,
    )
  }
}
